import React from "react";
import {
  ContactReferenceModel,
  UpsertPaymentModel,
  RelatedRefundsGridModel,
  PaymentMethodModel,
  MerchantAccount,
} from "../../../shared/models";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import AddIcon from "../../../../../../images/common/add-inside-circle-blue.svg";
import { firstValueFrom } from "rxjs";
import {
  CustomLabel,
  FlexContainer,
  Input,
  ModalComponent,
  SapphireBlueButton,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { PaymentService } from "../../../shared/services";
import {
  RenderContactInfo,
  RenderPaymentMethod,
} from "../../../shared/components";
import { InvoicePopupWrapper } from "../../../shared/styles";
import { AccountSettingsStore } from "../../../shared/store";

type IProps = {
  navigate: NavigateFunction;
  onClose: (refresh: boolean) => void;
  paymentData: UpsertPaymentModel;
  refunds: RelatedRefundsGridModel[];
};

const UpsertRefundPopUp: React.FC<IProps> = ({
  onClose,
  paymentData,
  refunds,
}) => {
  const [data, setPaymentData] = React.useState<UpsertPaymentModel>(
    new UpsertPaymentModel(paymentData)
  );
  const [showDescription, setShowDescription] = React.useState(false);
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);

  function row(label: string, rightSide: React.ReactNode, className?: string) {
    return (
      <FlexContainer
        spacing={30}
        margin="15px"
        className={`standard-lh ${className}`}
      >
        <CustomLabel lineHeight="normal">{label}</CustomLabel>
        <FlexContainer alignItems="center" spacing={20}>
          {rightSide}
        </FlexContainer>
      </FlexContainer>
    );
  }

  const formatAmount = (value: string): string => {
    return AccountSettingsStore.formatAmount(
      parseFloat(value.toString() || "0.00"),
      true
    );
  };

  function updateState(
    key: keyof UpsertPaymentModel,
    value:
      | string
      | boolean
      | number
      | Date
      | MerchantAccount
      | PaymentMethodModel,
    isBlur?: boolean
  ) {
    const clonedData = new UpsertPaymentModel(data);
    switch (key) {
      case "refundDescription":
        clonedData.refundDescription = value as string;
        break;
      case "refundAmount":
        if (isBlur) {
          value = formatAmount(value as string);
        }
        clonedData.refundAmount = value as string;
        break;
    }
    clonedData.validate(true);
    setPaymentData(clonedData);
  }

  function textInput(
    type: "text" | "textarea" | "number" | "email" | "date",
    key: keyof UpsertPaymentModel,
    value: string | Date,
    placeHolder: string,
    className: string,
    errorMessage: string
  ) {
    return (
      <Input
        type={type}
        value={value}
        error={data.hasError(key)}
        className={className}
        onChange={(e, isBlur) => updateState(key, e.target.value, isBlur)}
        placeholder={placeHolder}
        errorMessage={errorMessage}
      />
    );
  }

  async function onSubmit() {
    const success = data.validate();
    if (!success) {
      setPaymentData(new UpsertPaymentModel(data));
      return;
    }
    setAPIRequestPending(true);
    const response = await firstValueFrom(
      PaymentService.refundPayment(data.createRefundModel())
    );
    if (response.result.success) {
      ToastService.showSuccess("Refund created successfully");
      onClose(true);
    } else {
      setAPIRequestPending(false);
      ToastService.showError("Error in creating payment refund");
    }
  }

  function body() {
    return (
      <InvoicePopupWrapper className="subscription-popup">
        {row(
          "Customer",
          <RenderContactInfo
            contact={data.contact ?? new ContactReferenceModel()}
          />
        )}
        {row(
          "Merchant Account",
          <CustomLabel>{data.merchantAccount?.name}</CustomLabel>
        )}
        {row(
          "Credit Card",
          <RenderPaymentMethod paymentMethod={data.paymentMethod} />
        )}
        {row(
          "Subscription",
          <CustomLabel>
            {data.subscriptionName === "" ? "-" : data.subscriptionName}
          </CustomLabel>
        )}
        {row(
          "Invoice",
          <CustomLabel>
            {data.invoiceNumber === "" ? "-" : data.invoiceNumber}
          </CustomLabel>
        )}
        {row(
          "Paid",
          <CustomLabel>
            <span className="highlighted-black">${data.amount}</span> on{" "}
            <span className="highlighted-black">
              {UtilityMethods.formatDateInString(new Date(data.dueDate))}
            </span>
          </CustomLabel>
        )}

        {row(
          "Previously Refunded",
          <CustomLabel>
            {data.previousRefundAmount > 0 ? (
              <>
                <span className="highlighted-black">
                  ${data.previousRefundAmount}
                </span>{" "}
                on{" "}
                <span className="highlighted-black">
                  {UtilityMethods.formatDateInString(refunds[0].createdDate)}
                </span>
              </>
            ) : (
              "-"
            )}
          </CustomLabel>
        )}
        {row(
          "Refund Amount",
          <FlexContainer
            spacing={15}
            fxLayout="row"
            className="dollar-usd-input"
          >
            <div>
              {textInput(
                "text",
                "refundAmount",
                data.refundAmount,
                "0.00",
                "",
                "Please enter amount to be refunded"
              )}
            </div>
          </FlexContainer>,
          "input-label-lh"
        )}
        {row(
          "Description",
          showDescription ? (
            textInput(
              "textarea",
              "refundDescription",
              data.refundDescription,
              "Enter Description",
              "",
              "Please enter description"
            )
          ) : (
            <FlexContainer
              onClick={() => setShowDescription(true)}
              spacing={5}
              alignItems="center"
              style={{ cursor: "pointer" }}
            >
              <img src={AddIcon} className="referral-add-icon" alt="Add icon" />
              Add
            </FlexContainer>
          )
        )}
      </InvoicePopupWrapper>
    );
  }

  return (
    <ModalComponent
      title={"New Refund"}
      titleFontSize="22px"
      bodyPadding="40px 50px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="900px"
      bodyBackgroundColor="#fff"
      footerBackground="#F4F7FB"
      footerHeight="auto"
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <SapphireBlueButton
            minWidth="120px"
            height="30px"
            onClick={() => onClose(false)}
          >
            Cancel
          </SapphireBlueButton>
          <SapphireBlueButton
            disabled={isAPIRequestPending}
            primary
            minWidth="120px"
            height="30px"
            onClick={() => onSubmit()}
          >
            Refund Now
          </SapphireBlueButton>
        </FlexContainer>
      }
    >
      {body()}
    </ModalComponent>
  );
};

export default withRouter(observer(UpsertRefundPopUp));
